import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRequireAdmin } from "@/lib/useRequireAdmin";
import { optimizeImage } from "@/lib/mediaUrls";

const SLIDE_TYPES = [
  { value: "spotlight_of_week", label: "🌟 Spotlight of the Week" },
  { value: "spotlight",         label: "🌟 Spotlight Sundays (link to /spotlight)" },
  { value: "featured_push",     label: "🚀 Featured Push" },
  { value: "feature",           label: "🏆 Leaderboard / Most Pushed (links to /leaderboard/today)" },
  { value: "top_beam_host",     label: "🎙 Top Beam Host" },
  { value: "top_supporter",     label: "👑 Top Supporter" },
  { value: "gifts",             label: "🎁 Virtual Gifts (links to /virtual-gifts)" },
  { value: "submit",            label: "🎤 Submit Music (links to /submit)" },
  { value: "magazine",          label: "📰 Magazine (links to /magazine)" },
  { value: "interview_event",   label: "🎤 Interview / Event" },
  { value: "announcement",      label: "📣 Announcement" },
];

const empty = {
  type: "announcement",
  title: "",
  subtitle: "",
  image_url: "",
  cta_label: "",
  cta_url: "",
  start_at: "",
  end_at: "",
  active: true,
  order: 0,
};

const AdminHeroSlidesPage = () => {
  const { authChecked } = useRequireAdmin();
  const [slides, setSlides] = useState([]);
  const [editing, setEditing] = useState(null); // null OR slide_id OR "new"
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  // Phase 9.7.14 — direct Cloudinary uploader state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Phase 9.7.19 — preview modal state
  const [previewSlide, setPreviewSlide] = useState(null);

  // Phase 9.7.19 — helpers for the new datetime-local picker. The backend
  // stores ISO timestamps; <input type="datetime-local"> expects the
  // "YYYY-MM-DDTHH:mm" format. Convert both ways without losing seconds.
  const toLocalInputValue = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };
  const fromLocalInputValue = (local) => {
    if (!local) return "";
    try {
      const d = new Date(local);
      if (isNaN(d.getTime())) return "";
      return d.toISOString();
    } catch { return ""; }
  };

  // Phase 9.7.19 — status pill (Scheduled / Active / Paused / Expired)
  const slideStatus = (s) => {
    const now = new Date();
    const start = s.start_at ? new Date(s.start_at) : null;
    const end = s.end_at ? new Date(s.end_at) : null;
    if (!s.active) return { label: "Paused", cls: "bg-gray-700/60 text-gray-200 border-gray-500/40" };
    if (end && now > end) return { label: "Expired", cls: "bg-red-500/15 text-red-200 border-red-500/40" };
    if (start && now < start) return { label: "Scheduled", cls: "bg-sky-500/15 text-sky-200 border-sky-500/40" };
    return { label: "Active", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40" };
  };

  const handleBannerUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG/PNG/WebP).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/hero-slides/upload-banner", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.image_url;
      if (url) {
        setForm((f) => ({ ...f, image_url: url }));
        toast.success("Banner uploaded");
      } else {
        toast.error("Upload returned no URL");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const load = async () => {
    try {
      const r = await api.get("/admin/hero-slides");
      setSlides(r.data?.slides || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load slides");
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (slide) => {
    setForm({ ...empty, ...slide });
    setEditing(slide.slide_id);
  };
  const startCreate = () => {
    setForm({ ...empty, order: (slides[slides.length - 1]?.order || 0) + 1 });
    setEditing("new");
  };
  const cancelEdit = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title required");
      return;
    }
    setBusy(true);
    try {
      let savedId = editing;
      if (editing === "new") {
        const r = await api.post("/admin/hero-slides", form);
        savedId = r.data?.slide?.slide_id || null;
        toast.success("Slide created and published");
      } else {
        await api.patch(`/admin/hero-slides/${editing}`, form);
        toast.success("Slide updated");
      }
      cancelEdit();
      await load();
      // Phase 9.7.19 — scroll to the newly created/edited slide so the
      // founder visually confirms it's persisted in the dashboard list.
      if (savedId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="hero-row-${savedId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-amber-400");
            setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 2200);
          }
        }, 100);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    try {
      await api.delete(`/admin/hero-slides/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/hero-slides/${s.slide_id}`, { active: !s.active });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const move = async (s, dir) => {
    const otherIdx = slides.findIndex((x) => x.slide_id === s.slide_id) + dir;
    if (otherIdx < 0 || otherIdx >= slides.length) return;
    const other = slides[otherIdx];
    try {
      await api.patch(`/admin/hero-slides/${s.slide_id}`, { order: other.order });
      await api.patch(`/admin/hero-slides/${other.slide_id}`, { order: s.order });
      await load();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  if (!authChecked) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0e] py-8 px-4 sm:px-6" data-testid="admin-hero-slides-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin"><Button variant="ghost" className="text-purple-300 mb-4">← Admin</Button></Link>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">🎬 Home Hero / Spotlight Carousel</h1>
            <p className="text-pink-200/70 text-sm">Author the rotating hero banner shown at the top of /home.</p>
          </div>
          <Button
            onClick={startCreate}
            className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
            data-testid="hero-create-btn"
          >
            + New Slide
          </Button>
        </div>

        {editing && (
          <Card className="p-5 bg-[#101019] border-amber-500/30 mb-6" data-testid="hero-edit-form">
            <h2 className="text-lg font-bold text-white mb-3">{editing === "new" ? "New slide" : "Edit slide"}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-gray-300">Type</Label>
                <select
                  className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="hero-form-type"
                >
                  {SLIDE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-gray-300">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="hero-form-title" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-gray-300">Subtitle</Label>
                <Textarea value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} rows={2} data-testid="hero-form-subtitle" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-gray-300">Banner image</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBannerUpload}
                    className="hidden"
                    data-testid="hero-form-image-file"
                  />
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-purple-600 hover:bg-purple-500 text-white"
                    data-testid="hero-form-upload-btn"
                  >
                    {uploading ? "Uploading…" : "📤 Upload"}
                  </Button>
                  <Input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="…or paste a Cloudinary URL"
                    data-testid="hero-form-image-url"
                  />
                </div>
                {form.image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-white/10 max-w-md">
                    <img
                      src={optimizeImage(form.image_url, 'cover')}
                      alt="banner preview"
                      className="w-full h-32 object-cover"
                      data-testid="hero-form-image-preview"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-gray-300">CTA label</Label>
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="Listen Now" data-testid="hero-form-cta-label" />
              </div>
              <div>
                <Label className="text-gray-300">CTA URL</Label>
                <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="/track/sub_..." data-testid="hero-form-cta-url" />
              </div>
              <div>
                <Label className="text-gray-300">Start at (optional)</Label>
                <Input
                  type="datetime-local"
                  value={toLocalInputValue(form.start_at)}
                  onChange={(e) => setForm({ ...form, start_at: fromLocalInputValue(e.target.value) })}
                  data-testid="hero-form-start-at"
                  className="bg-black/40 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">End at (optional)</Label>
                <Input
                  type="datetime-local"
                  value={toLocalInputValue(form.end_at)}
                  onChange={(e) => setForm({ ...form, end_at: fromLocalInputValue(e.target.value) })}
                  data-testid="hero-form-end-at"
                  className="bg-black/40 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="hero-form-active" />
                <Label className="text-gray-300">Active</Label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="hero-save-btn">
                {busy ? "Saving..." : "Save"}
              </Button>
              <Button onClick={cancelEdit} variant="outline" className="text-white border-gray-700">Cancel</Button>
            </div>
          </Card>
        )}

        <div className="space-y-3" data-testid="hero-slides-list">
          {slides.length === 0 && (
            <p className="text-gray-400 text-center py-12">No slides yet. Click <strong>+ New Slide</strong> to author one.</p>
          )}
          {slides.map((s, i) => {
            const status = slideStatus(s);
            return (
            <Card key={s.slide_id} className="p-4 bg-[#101019] border-white/10" data-testid={`hero-row-${s.slide_id}`}>
              <div className="flex items-start gap-4">
                {s.image_url ? (
                  <img src={optimizeImage(s.image_url, 'avatar')} alt="" className="w-24 h-16 object-cover rounded shrink-0" />
                ) : (
                  <div className="w-24 h-16 bg-purple-500/20 rounded flex items-center justify-center text-2xl shrink-0">🎬</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs uppercase tracking-wider text-amber-200 font-bold">
                      {(SLIDE_TYPES.find((t) => t.value === s.type) || {}).label || s.type}
                    </p>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${status.cls}`}
                      data-testid={`hero-status-${s.slide_id}`}
                    >
                      {status.label}
                    </span>
                  </div>
                  <p className="text-white font-bold truncate" data-testid={`hero-row-title-${s.slide_id}`}>{s.title}</p>
                  {s.subtitle && <p className="text-pink-200/70 text-sm truncate">{s.subtitle}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    order: {s.order}
                    {s.end_at && <> · ends {new Date(s.end_at).toLocaleString()}</>}
                    {s.start_at && <> · starts {new Date(s.start_at).toLocaleString()}</>}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => move(s, -1)} disabled={i === 0} className="text-white border-gray-700 px-2" data-testid={`hero-move-up-${s.slide_id}`}>↑</Button>
                    <Button size="sm" variant="outline" onClick={() => move(s, +1)} disabled={i === slides.length - 1} className="text-white border-gray-700 px-2" data-testid={`hero-move-down-${s.slide_id}`}>↓</Button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setPreviewSlide(s)} className="text-sky-200 border-sky-400/40" data-testid={`hero-preview-${s.slide_id}`}>
                    👁 Preview
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(s)} className="text-white border-gray-700" data-testid={`hero-toggle-${s.slide_id}`}>
                    {s.active ? "⏸ Pause" : "▶ Activate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(s)} className="text-amber-200 border-amber-400/40" data-testid={`hero-edit-${s.slide_id}`}>
                    ✏️ Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(s.slide_id)} className="text-red-300 border-red-500/40" data-testid={`hero-delete-${s.slide_id}`}>
                    🗑 Delete
                  </Button>
                </div>
              </div>
            </Card>
          );
          })}
        </div>

        {/* Phase 9.7.19 — Preview modal: shows the slide exactly how it
            would render in the home hero carousel (cover image + title +
            subtitle + CTA). Read-only. */}
        {previewSlide && (
          <div
            className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPreviewSlide(null)}
            data-testid="hero-preview-modal"
          >
            <div
              className="max-w-3xl w-full bg-[#0a0a0e] rounded-2xl overflow-hidden border border-amber-400/40 shadow-2xl shadow-amber-500/20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative aspect-[16/7] bg-black">
                {previewSlide.image_url ? (
                  <img src={optimizeImage(previewSlide.image_url, 'cover')} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-6xl">🎬</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-1">
                    {(SLIDE_TYPES.find((t) => t.value === previewSlide.type) || {}).label || previewSlide.type}
                  </p>
                  <h2 className="text-2xl sm:text-3xl text-white font-black drop-shadow-lg" data-testid="hero-preview-title">
                    {previewSlide.title}
                  </h2>
                  {previewSlide.subtitle && (
                    <p className="text-pink-100/90 text-sm mt-1 max-w-xl">
                      {previewSlide.subtitle}
                    </p>
                  )}
                  {previewSlide.cta_label && (
                    <button
                      className="mt-3 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 text-white text-sm font-bold inline-flex items-center gap-2"
                      disabled
                      data-testid="hero-preview-cta"
                    >
                      {previewSlide.cta_label} →
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between p-4 border-t border-white/10 text-xs text-gray-400">
                <span>This is exactly how the slide will appear on /home.</span>
                <Button
                  onClick={() => setPreviewSlide(null)}
                  variant="outline"
                  className="text-white border-gray-700"
                  data-testid="hero-preview-close"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHeroSlidesPage;
